import { useWatch } from 'react-hook-form';
import type { Control, FieldValues, Path } from 'react-hook-form';

import {
  KINNSER_ADMISSION_SOURCE_VALUES,
  UNSUPPORTED_ADMISSION_SOURCE_LABELS,
} from '../../hooks/useCreatePatientForm';

/**
 * The shared, non-component half of the Kinnser Admission Source field: the
 * form-field contract, the intake shape the notice reads, and the precedence
 * that decides which unsendable value (if any) the notice names.
 *
 * Split out of `KinnserAdmissionSourceField.tsx` so that file exports a
 * component and nothing else — mixing the two breaks React Fast Refresh, and
 * the row-level hint in `KinnserReferralSourceFields` needs this logic without
 * rendering the select.
 */

export const ADMISSION_SOURCE_FIELD = 'kinnser_admission_source';

/** The single field a host form must carry to render this select. */
export interface KinnserAdmissionSourceFormFields {
  kinnser_admission_source?: string | null;
}

/** The intake values the notice reads to name an unsendable captured value. */
export interface AdmissionSourceIntakeContext {
  discharge_facility_type?: string | null;
  kinnser_admission_source?: string | null;
  fields?: Record<string, unknown> | null;
}

/**
 * Which unsendable admission source, if any, the notice should name.
 *
 * The intake can capture an admission source WellSky's home-health select
 * cannot represent, in which case it is not in the dropdown. Say so rather
 * than omitting it silently, or the screen would imply the captured value is
 * on its way to WellSky when the push is really sending nothing.
 *
 * The test is "not one of the sendable codes", NOT "is D, E or F". Intake's
 * discharge_facility_type is free text in practice: production holds values
 * like "Short-stay Acute Hospital (IPPS)" and "NA - Not Discharged from an
 * Inpatient Facility", which vastly outnumber D/E/F. Scoping this to the three
 * codes left those intakes with an empty select and no explanation.
 *
 * Pure and exported so the row-level hint can ask the same question without a
 * second copy of the precedence.
 */
export function resolveUnsupportedAdmissionSource(
  selectedIntake: AdmissionSourceIntakeContext | null | undefined,
  currentValue: string
): { label?: string; fromOverride: boolean } {
  const captured = String(
    selectedIntake?.fields?.discharge_facility_type ??
      selectedIntake?.discharge_facility_type ??
      ''
  );
  // A saved override wins outright over the captured value, including the
  // empty string, exactly as `resolve_kinnser_admission_source` resolves it.
  // So the value that would actually reach WellSky is this one, and it is the
  // one that has to be checked -- reading only the captured value missed a
  // saved override the server drops, which left the select blank with nothing
  // explaining it. IntakeUpdate rejects such an override now, so this covers
  // rows written before that validator or by another writer.
  const saved = selectedIntake?.kinnser_admission_source;
  const persisted =
    saved !== null && saved !== undefined ? String(saved) : captured;

  const isUnsendable = (value: string) =>
    value !== '' && !KINNSER_ADMISSION_SOURCE_VALUES.has(value);

  // Name whichever unsendable value is in play. The override is checked first
  // because it is what the push resolves to; the captured value is still worth
  // naming behind an explicit clear (saved ''), where the push sends nothing
  // and the intake's own value would otherwise go unmentioned.
  const unsendable = isUnsendable(persisted)
    ? persisted
    : isUnsendable(captured)
      ? captured
      : '';

  // A picked value supersedes the notice: the coordinator has answered it.
  if (currentValue || unsendable === '') {
    return { label: undefined, fromOverride: false };
  }

  // Own-property check, because the key comes from untyped JSONB: a captured
  // value of "toString" would otherwise resolve up the prototype chain and
  // render the function source as the label. `Object.hasOwn` would need the
  // ES2022 lib and this project targets ES2020, so this uses the same
  // hasOwnProperty.call form as src/components/task/taskTypeFilters.ts.
  // Free-text values have no label, so they are shown as captured.
  const label = Object.prototype.hasOwnProperty.call(
    UNSUPPORTED_ADMISSION_SOURCE_LABELS,
    unsendable
  )
    ? UNSUPPORTED_ADMISSION_SOURCE_LABELS[unsendable]
    : unsendable;

  return { label, fromOverride: unsendable !== captured };
}

/**
 * What the row's single "left unset" line needs to know about this field.
 *
 * `isUnset` keeps that line up while the admission source is still blank, even
 * once both referrals are filled. `explainedByNotice` takes it down when the
 * amber notice above is already stating the same outcome, so the row never
 * shows two sentences saying one thing.
 */
export function useAdmissionSourceRowHintState<
  T extends FieldValues & KinnserAdmissionSourceFormFields,
>(
  control: Control<T>,
  selectedIntake: AdmissionSourceIntakeContext | null | undefined
): { isUnset: boolean; explainedByNotice: boolean } {
  const value =
    (useWatch({ control, name: ADMISSION_SOURCE_FIELD as Path<T> }) as
      string | null | undefined) ?? '';
  const { label } = resolveUnsupportedAdmissionSource(selectedIntake, value);
  return { isUnset: !value, explainedByNotice: Boolean(label) };
}
